import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Card, Modal } from '@/components/kit';
import apiClient from '@/services/apiClient';

/**
 * "Generate a theme with AI" — the Theme board's accent card, opened.
 *
 * A brand colour and a vibe go in; the recommender answers with two or three
 * {primary, accent} pairs, and picking one sets the two colour slots on the
 * DRAFT. Nothing is applied to the clinic until Save, which is why the card's
 * own caption says "previewed before it applies".
 *
 * IT IS ALLOWED TO BE UNAVAILABLE. The recommender sits behind the tenant's AI
 * entitlement, exactly as the owner's copy of it does — a clinic past its trial
 * is told so in plain words instead of being shown a spinner that never ends
 * (the AI-optional EMR rule). Its own gate answers `AI_NOT_ENTITLED`, and that
 * is what this prints.
 */

export interface ThemeSeed {
  name: string;
  primary: string;
  accent: string;
}

const HEX = /^#[0-9a-fA-F]{6}$/;

export function ThemeAiModal({
  open,
  onClose,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (seed: ThemeSeed) => void;
}) {
  const [colour, setColour] = useState('');
  const [vibe, setVibe] = useState('');
  const [state, setState] = useState<'idle' | 'asking' | 'done' | 'failed'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [seeds, setSeeds] = useState<ThemeSeed[]>([]);

  if (!open) return null;

  const ask = async () => {
    setState('asking');
    setError(null);
    try {
      const res = await apiClient.post('/clinic-settings/branding/suggest-theme', {
        seedColors: HEX.test(colour.trim()) ? [colour.trim()] : undefined,
        description: vibe.trim() || undefined,
      });
      const rows = ((res.data?.data ?? res.data) ?? []) as ThemeSeed[];
      setSeeds(rows);
      setState('done');
    } catch (e) {
      const body = (e as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error;
      setError(body?.message ?? 'The recommender did not answer. Nothing was changed.');
      setState('failed');
    }
  };

  /* THE DIM IS THE WHOLE SCREEN, NOT THE COLUMN.

     The kit's `.dscrim` is `position:absolute; inset:0`, which on an artboard
     is the whole 1440×1024 frame because the artboard IS the frame. In the app
     the modal is rendered inside the page root, which lives inside the shell's
     scrolling `<main>` — so the dim stopped at the content column and left the
     rail, the top bar and the docked pane bright, i.e. still looking live while
     a dialog is open over them. Portalled to <body> inside a fixed `.maie desk`
     frame, the scrim covers what the canvas covers, and the dialog centres on
     the screen rather than on the column. The frame exists only while the
     modal is open, so nothing is over the desk the rest of the time. */
  return createPortal(
    <div
      className="maie desk"
      style={{ position: 'fixed', inset: 0, zIndex: 60 }}
    >
    <Modal
      open={open}
      onClose={onClose}
      title="Generate a theme with AI"
      sub="a brand colour and a vibe → a matched light and dark pair, previewed before it applies"
      width={480}
      foot={(
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Close</button>
          <button type="button" className="btn btn-primary"
            disabled={state === 'asking' || (!HEX.test(colour.trim()) && !vibe.trim())}
            onClick={() => { void ask(); }}>
            {state === 'asking' ? 'Asking…' : 'Suggest palettes'}
          </button>
        </>
      )}
    >
      <label className="t-ovl mut" htmlFor="cm-theme-colour" style={{ display: 'block', marginTop: 12 }}>
        Brand colour
      </label>
      <input id="cm-theme-colour" className="cm-inp" value={colour} placeholder="#0f766e"
        onChange={(e) => setColour(e.target.value)} />
      <label className="t-ovl mut" htmlFor="cm-theme-vibe" style={{ display: 'block', marginTop: 12 }}>
        The vibe
      </label>
      <input id="cm-theme-vibe" className="cm-inp" value={vibe}
        placeholder="calm pediatric teal, warm and friendly"
        onChange={(e) => setVibe(e.target.value)} />
      <p className="t-cap mut mt8">
        One of the two is enough. Either seeds the palette; both together steer it harder.
      </p>

      {state === 'failed' ? (
        <Card kind="warn" style={{ marginTop: 12 }}>
          <p className="t-sec">{error}</p>
        </Card>
      ) : null}

      {state === 'done' && seeds.length === 0 ? (
        <p className="t-sec mut" style={{ marginTop: 12 }}>
          The recommender had nothing to suggest for that. Try a colour, or say more about the
          feeling you are after.
        </p>
      ) : null}

      {seeds.map((s) => (
        <Card key={`${s.name}-${s.primary}`} style={{ marginTop: 8, cursor: 'pointer' }}
          onClick={() => { onPick(s); onClose(); }}>
          <div className="flex" style={{ gap: 10, alignItems: 'center' }}>
            <span aria-hidden="true" style={{
              width: 22, height: 22, borderRadius: 6, background: s.primary,
              border: '1px solid var(--stroke-200)',
            }} />
            <span aria-hidden="true" style={{
              width: 22, height: 22, borderRadius: 6, background: s.accent,
              border: '1px solid var(--stroke-200)',
            }} />
            <div className="grow">
              <p className="t-strong">{s.name}</p>
              <p className="t-cap mut">{s.primary} · {s.accent}</p>
            </div>
            <span className="t-cap brand w6">Preview it</span>
          </div>
        </Card>
      ))}
    </Modal>
    </div>,
    document.body,
  );
}
